import { MouseEvent, ReactNode } from 'react';

import {
  Box,
  Divider,
  mergeSx,
  Stack,
  SxProps,
  tokens,
  Typography,
  TypographyProps,
} from '@cast/design-system';
import clsx from 'clsx';

import { inventoryListItemClassNames } from './classNames';

type InventoryListItemSize = 'md' | 'sm';

type InventoryListItemProps = {
  /** Additional CSS class */
  className?: string;
  /** `data-testid` attribute value */
  testId?: string;
  /** MUI sx prop */
  sx?: SxProps;
  /** Visual size of the list item */
  size?: InventoryListItemSize;
  /** Whether to show a divider below the item */
  divider?: boolean;
  /** Click handler. When provided, the row becomes keyboard-focusable and shows a pointer cursor. */
  onClick?: (event: MouseEvent<HTMLDivElement>) => void;
  /** Dynamic content slots */
  slots?: {
    /** Content rendered at the start of the item. Defaults to a square marker. Pass `null` to hide. */
    startContent?: ReactNode;
    /** Primary label */
    label?: ReactNode;
    /** Secondary description */
    description?: ReactNode;
    /** Content rendered at the end of the item */
    endContent?: ReactNode;
  };
  /** Props applied to the default Typography wrappers when a slot is a string or number. */
  slotProps?: {
    label?: Partial<TypographyProps>;
    description?: Partial<TypographyProps>;
    endContent?: Partial<TypographyProps>;
  };
};

const DEFAULT_MARKER_SIZE = 8;

const DefaultMarker = () => (
  <Box
    sx={{
      width: DEFAULT_MARKER_SIZE,
      height: DEFAULT_MARKER_SIZE,
      minWidth: DEFAULT_MARKER_SIZE,
      minHeight: DEFAULT_MARKER_SIZE,
      borderRadius: tokens.theme.radius.xs,
      backgroundColor: tokens.theme.data.grey900,
    }}
  />
);

const sizeConfig: Record<
  InventoryListItemSize,
  {
    py: number;
    gap: number;
    labelVariant: 'P12R';
    descriptionVariant: 'P12R';
    endContentVariant: 'P14R' | 'P12R';
  }
> = {
  md: {
    py: 8,
    gap: 12,
    labelVariant: 'P12R',
    descriptionVariant: 'P12R',
    endContentVariant: 'P14R',
  },
  sm: {
    py: 6,
    gap: 6,
    labelVariant: 'P12R',
    descriptionVariant: 'P12R',
    endContentVariant: 'P12R',
  },
};

/**
 * A reusable list item for inventory dashboards.
 * Supports a start marker, label, description, and trailing value content.
 */
export const InventoryListItem = ({
  className,
  testId,
  sx,
  size = 'md',
  divider = false,
  onClick,
  slots,
  slotProps,
}: InventoryListItemProps) => {
  const config = sizeConfig[size];
  const interactive = Boolean(onClick);

  return (
    <Box
      className={clsx(inventoryListItemClassNames.root, className)}
      data-testid={testId}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={onClick}
      sx={mergeSx(
        {
          transition: 'background-color 150ms',
          cursor: interactive ? 'pointer' : undefined,
          '&:hover': {
            backgroundColor: tokens.theme.bg.action.surfaceHover,
          },
        },
        sx
      )}
    >
      <Stack
        direction="row"
        alignItems="center"
        gap={config.gap}
        py={config.py}
        px={4}
      >
        <Box className={inventoryListItemClassNames.startContent}>
          {slots?.startContent === undefined ? (
            <DefaultMarker />
          ) : (
            slots.startContent
          )}
        </Box>

        <Stack flex={1} minWidth={0} overflow="hidden">
          {slots?.label &&
            (typeof slots.label === 'string' ||
            typeof slots.label === 'number' ? (
              <Typography
                className={inventoryListItemClassNames.label}
                variant={config.labelVariant}
                noWrap
                {...slotProps?.label}
              >
                {slots.label}
              </Typography>
            ) : (
              <Box className={inventoryListItemClassNames.label}>
                {slots.label}
              </Box>
            ))}
          {slots?.description &&
            (typeof slots.description === 'string' ||
            typeof slots.description === 'number' ? (
              <Typography
                className={inventoryListItemClassNames.description}
                variant={config.descriptionVariant}
                color={tokens.theme.fg.structural.secondary}
                noWrap
                {...slotProps?.description}
              >
                {slots.description}
              </Typography>
            ) : (
              <Box className={inventoryListItemClassNames.description}>
                {slots.description}
              </Box>
            ))}
        </Stack>

        {slots?.endContent && (
          <Box className={inventoryListItemClassNames.endContent}>
            {typeof slots.endContent === 'string' ||
            typeof slots.endContent === 'number' ? (
              <Typography
                variant={config.endContentVariant}
                noWrap
                {...slotProps?.endContent}
              >
                {slots.endContent}
              </Typography>
            ) : (
              slots.endContent
            )}
          </Box>
        )}
      </Stack>
      {divider && <Divider />}
    </Box>
  );
};
